/**
 * The error model every caller switches on. `ApiError` is the factory's normalized error: `code`
 * is tmm-api's `error.code` (design §8.2) or one of the factory's own (`SESSION_EXPIRED`,
 * `NETWORK_ERROR`, `UNKNOWN`); `status` is the HTTP status (0 = transport failure).
 */
export { ApiError } from './httpClientFactory';
import { ApiError } from './httpClientFactory';

export const ErrorCodes = {
  SUBSCRIPTION_IN_PROGRESS: 'SUBSCRIPTION_IN_PROGRESS',
  SUBSCRIPTION_EXISTS: 'SUBSCRIPTION_EXISTS',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN: 'UNKNOWN',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
  SUBSCRIPTION_EXPIRED: 'SUBSCRIPTION_EXPIRED',
  SUBSCRIPTION_CREATE_FAILED: 'SUBSCRIPTION_CREATE_FAILED',
  PAY_API_UNAVAILABLE: 'PAY_API_UNAVAILABLE',
  PLAN_MAPPING_CONFLICT: 'PLAN_MAPPING_CONFLICT',
  PROMOTION_NOT_FOUND: 'PROMOTION_NOT_FOUND',
  PROMOTION_NOT_REDEEMABLE: 'PROMOTION_NOT_REDEEMABLE',
  // tmm-api 0150: the code's last redemption was just taken (decided before any charge).
  EXHAUSTED: 'EXHAUSTED',
  REFERRAL_NOT_FOUND: 'REFERRAL_NOT_FOUND',
  REFERRAL_CONFIRM_REQUIRED: 'REFERRAL_CONFIRM_REQUIRED',
  // MM-D-138 (TMMAPI-0156 U1): a subscribe without the current terms version and auto-renewal
  // consent; `details.currentTermsVersion` is the version to accept. Nothing was created.
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  SUBSCRIPTION_PENDING_ACTION: 'SUBSCRIPTION_PENDING_ACTION',
  PAYMENT_METHOD_UPDATE_FAILED: 'PAYMENT_METHOD_UPDATE_FAILED',
  DELETION_ALREADY_REQUESTED: 'DELETION_ALREADY_REQUESTED',
  // TMMAPI-0157: Delete now / Start over sent a confirmation that isn't the account's email.
  CONFIRMATION_REQUIRED: 'CONFIRMATION_REQUIRED',
  AI_DISABLED: 'AI_DISABLED',
  AI_BUDGET_EXCEEDED: 'AI_BUDGET_EXCEEDED',
  // tmm-api 0151 (MM-D-131): a wallet purchase or top-up the card declined, or that can't be charged.
  WALLET_CHARGE_FAILED: 'WALLET_CHARGE_FAILED',
  WALLET_CHARGE_UNAVAILABLE: 'WALLET_CHARGE_UNAVAILABLE',
  IMPORT_FILE_TOO_LARGE: 'IMPORT_FILE_TOO_LARGE',
  IMPORT_UNPARSEABLE: 'IMPORT_UNPARSEABLE',
  IMPORT_MAPPING_INVALID: 'IMPORT_MAPPING_INVALID',
  IMPORT_BATCH_STATE: 'IMPORT_BATCH_STATE',
  IMPORT_PRESET_NOT_FOUND: 'IMPORT_PRESET_NOT_FOUND',
  FX_RATE_UNAVAILABLE: 'FX_RATE_UNAVAILABLE',
  PROFILE_NAME_TAKEN: 'PROFILE_NAME_TAKEN',
  PROFILE_NOT_FOUND: 'PROFILE_NOT_FOUND',
  USER_FROZEN: 'USER_FROZEN',
  REVIEW_NOT_DISMISSABLE: 'REVIEW_NOT_DISMISSABLE',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  RATE_LIMITED: 'RATE_LIMITED',
  UPSTREAM_UNAVAILABLE: 'UPSTREAM_UNAVAILABLE',
  HOSTNAME_NOT_REGISTERED: 'HOSTNAME_NOT_REGISTERED',
  TENANT_AUTH_NOT_CONFIGURED: 'TENANT_AUTH_NOT_CONFIGURED',
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  HOLDING_NOT_FOUND: 'HOLDING_NOT_FOUND',
  ACCOUNT_CLOSED: 'ACCOUNT_CLOSED',
  ACCOUNT_CURRENCY_IMMUTABLE: 'ACCOUNT_CURRENCY_IMMUTABLE',
  // TMMAPI-0160 U2: an import's `opening` on an account whose opening isn't pending, and an opening
  // dated on or after the account's earliest posted line (`details.earliestPostedOn`).
  OPENING_ALREADY_SET: 'OPENING_ALREADY_SET',
  OPENING_AFTER_FIRST_LINE: 'OPENING_AFTER_FIRST_LINE',
  // TMMAPI-0196 U3: a register line dated on or before the account's settled opening date
  // (`details.openingOn`): it is already in the opening balance.
  BEFORE_OPENING: 'BEFORE_OPENING',
  // TMMAPI-0191: the earlier history's new opening disagrees with the file's starting balance and
  // the commit didn't set `acceptOpeningDifference` (`details.differenceMinor`).
  OPENING_DIFFERS: 'OPENING_DIFFERS',
  // TMMAPI-0165 (MM-D-144): confirm or a by-hand check while the opening is not set, and
  // adjust / dismiss of a check that is no longer open.
  OPENING_PENDING: 'OPENING_PENDING',
  BALANCE_CHECK_NOT_OPEN: 'BALANCE_CHECK_NOT_OPEN',
  ACCOUNT_NOT_EMPTY: 'ACCOUNT_NOT_EMPTY',
  GRANTEE_NOT_SUBSCRIBER: 'GRANTEE_NOT_SUBSCRIBER',
  CURRENCY_LOCKED: 'CURRENCY_LOCKED',
  TRANSACTION_NOT_FOUND: 'TRANSACTION_NOT_FOUND',
  TRANSACTION_VOID: 'TRANSACTION_VOID',
  TRANSACTION_HAS_ALLOCATIONS: 'TRANSACTION_HAS_ALLOCATIONS',
  ALLOCATION_NOT_FOUND: 'ALLOCATION_NOT_FOUND',
  ALLOCATION_EXCEEDS_REMAINDER: 'ALLOCATION_EXCEEDS_REMAINDER',
  ALLOCATION_SIGN_MISMATCH: 'ALLOCATION_SIGN_MISMATCH',
  ALLOCATION_ALREADY_REVERSED: 'ALLOCATION_ALREADY_REVERSED',
  // tmm-api 0145 (MM-D-113 / 114 / 125): the re-split, assign and envelope-move answers.
  SPLIT_TOTAL_CHANGED: 'SPLIT_TOTAL_CHANGED',
  INSUFFICIENT_TO_ASSIGN: 'INSUFFICIENT_TO_ASSIGN',
  ENVELOPE_HAS_CONTRIBUTIONS: 'ENVELOPE_HAS_CONTRIBUTIONS',
  // tmm-api 0150: a write into an envelope that is being moved to another virtual account right now.
  CONCURRENT_UPDATE: 'CONCURRENT_UPDATE',
  CARD_PAYMENT_NOT_ALLOCATABLE: 'CARD_PAYMENT_NOT_ALLOCATABLE',
  PERMISSION_REQUIRED: 'PERMISSION_REQUIRED',
  IDEMPOTENCY_CONFLICT: 'IDEMPOTENCY_CONFLICT',
  SERIES_NOT_FOUND: 'SERIES_NOT_FOUND',
  SERIES_INVALID: 'SERIES_INVALID',
  PLAN_EXCEEDS_OCCURRENCE: 'PLAN_EXCEEDS_OCCURRENCE',
  OCCURRENCE_RESOLVED: 'OCCURRENCE_RESOLVED',
  // TMMAPI-0202 (MM-D-154): a plan line that repeats a bank series' slice; resent with
  // `confirmDuplicate: true` it saves (`details` {seriesName, accountName, amountMinor, nextOn}).
  PLAN_LINE_LIKELY_DUPLICATE: 'PLAN_LINE_LIKELY_DUPLICATE',
  OCCURRENCE_NOT_FOUND: 'OCCURRENCE_NOT_FOUND',
  MATCH_AMBIGUOUS: 'MATCH_AMBIGUOUS',
  NOT_AUTO_POST: 'NOT_AUTO_POST',
  COVERAGE_BACKWARD: 'COVERAGE_BACKWARD',
  ENVELOPE_KIND_MISMATCH: 'ENVELOPE_KIND_MISMATCH',
  MEETING_NOT_FOUND: 'MEETING_NOT_FOUND',
  INVITEE_NOT_SHARER: 'INVITEE_NOT_SHARER',
  // TMMAPI-0188 (MM-D-150): a standalone meeting's invitee outside the caller's sharing pool
  // (`details.userId`), and a caller who may not schedule one (neither a subscriber nor a Manager on
  // a virtual account every invitee is on).
  INVITEE_NOT_IN_POOL: 'INVITEE_NOT_IN_POOL',
  MEETING_SCHEDULE_NOT_ALLOWED: 'MEETING_SCHEDULE_NOT_ALLOWED',
  NOTE_VERSION_CONFLICT: 'NOTE_VERSION_CONFLICT',
  SUMMARY_NO_INPUT: 'SUMMARY_NO_INPUT',
  RULE_INVALID: 'RULE_INVALID',
  RULE_NOT_FOUND: 'RULE_NOT_FOUND',
  RULE_EXISTS: 'RULE_EXISTS',
  REORDER_SET_MISMATCH: 'REORDER_SET_MISMATCH',
  CARD_DECLINED: 'CARD_DECLINED',
  INTERVAL_NOT_OFFERED: 'INTERVAL_NOT_OFFERED',
  CONNECTOR_UNAVAILABLE: 'CONNECTOR_UNAVAILABLE',
  CONNECTOR_NOT_FOUND: 'CONNECTOR_NOT_FOUND',
  CONNECTOR_MAPPING_INVALID: 'CONNECTOR_MAPPING_INVALID',
  CONNECTOR_INSTITUTION_MISMATCH: 'CONNECTOR_INSTITUTION_MISMATCH',
  CONNECTOR_NOT_REAUTHABLE: 'CONNECTOR_NOT_REAUTHABLE',
  CONNECTOR_SESSION_NOT_FOUND: 'CONNECTOR_SESSION_NOT_FOUND',
  AUTOMATION_KEY_UNKNOWN: 'AUTOMATION_KEY_UNKNOWN',
  // tmm-api 0146 (MM-D-115 / 116 / 120): the owner's access, a contribution's currency, the summary.
  VIRTUAL_ACCOUNT_READ_ONLY: 'VIRTUAL_ACCOUNT_READ_ONLY',
  VIRTUAL_ACCOUNT_LOCKED: 'VIRTUAL_ACCOUNT_LOCKED',
  // tmm-api 0153 §F (MM-D-135): a write that needs a virtual account and the person has none (or
  // several, for genesis envelope proposals sent without one; `details.virtualAccounts`).
  VIRTUAL_ACCOUNT_REQUIRED: 'VIRTUAL_ACCOUNT_REQUIRED',
  ALLOCATION_CURRENCY_MISMATCH: 'ALLOCATION_CURRENCY_MISMATCH',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  // tmm-api 0156 U2 (MM-D-139): pay-api has no usable billing location for the card; nothing charged.
  TAX_LOCATION_REQUIRED: 'TAX_LOCATION_REQUIRED',
  // tmm-api 0157: a manual payback link of own-account money, a card payment, interest or a credit
  // on a card or loan account (`details.reason`); a claim whose mask isn't the account's last four.
  NOT_A_PAYBACK: 'NOT_A_PAYBACK',
  TRANSFER_NOT_ALLOCATABLE: 'TRANSFER_NOT_ALLOCATABLE',
  // TMMAPI-0179: a payback confirmed on a purchase with no envelope, sent without `envelopeId`
  // (409). The confirm paths ask for the envelope in place and resend (TMMAPP-0140 item 3).
  REIMBURSEMENT_TARGET_REQUIRED: 'REIMBURSEMENT_TARGET_REQUIRED',
  // tmm-api handoff 2026-10-21 (TMMAPP-0162 item 7): the purchase is already paid back by the
  // paybacks confirmed on it (409; `details` carries figures for logs, never copy).
  REIMBURSEMENT_EXCEEDS_EXPENSE: 'REIMBURSEMENT_EXCEEDS_EXPENSE',
  MASK_MISMATCH: 'MASK_MISMATCH',
  // TMMAPI-0165: a payee made from a wire that isn't one, or that names the owner.
  NOT_A_WIRE: 'NOT_A_WIRE',
  OWN_ACCOUNT_WIRE: 'OWN_ACCOUNT_WIRE',
  // TMMAPI-0159 (pay-api commerce adoption; TMMAPP-0122 item 23), keyed by code ahead of the
  // re-vendor: an AI credit charge refused while a dispute on an earlier wallet charge is open
  // (409), and a charge the person starts refused for a setup problem on our side (503, not
  // retryable).
  WALLET_DISPUTE_HOLD: 'WALLET_DISPUTE_HOLD',
  BILLING_CONFIGURATION: 'BILLING_CONFIGURATION',
  // TMMAPI-0160 U3: an alias pattern that can't be used (`details.reason` SYNTAX | TOO_LONG |
  // TOO_SLOW; `details.source` AI when the model wrote it), and an archived payee's aliases.
  PATTERN_INVALID: 'PATTERN_INVALID',
  PAYEE_ARCHIVED: 'PAYEE_ARCHIVED',
  // TMMAPI-0174: the model answered and nothing was usable (422, not retryable), or the call failed
  // (503, retryable); `details.reason` names why. `AI_DISABLED` is only the gate's refusal.
  AI_NO_SUGGESTION: 'AI_NO_SUGGESTION',
  AI_PROVIDER_UNAVAILABLE: 'AI_PROVIDER_UNAVAILABLE',
  // TMMAPI-0174 §G: a restore of an accepted suggestion, or of one that can never be a payee (409),
  // and another owner's or an unknown suggestion (404).
  PAYEE_SUGGESTION_DECIDED: 'PAYEE_SUGGESTION_DECIDED',
  PAYEE_SUGGESTION_NOT_FOUND: 'PAYEE_SUGGESTION_NOT_FOUND',
  // TMMAPI-0193 U1: every naming worker on the node is busy (503, retryable), and a run that isn't
  // the caller's or never was (404).
  PAYEE_NAMING_BUSY: 'PAYEE_NAMING_BUSY',
  PAYEE_NAMING_RUN_NOT_FOUND: 'PAYEE_NAMING_RUN_NOT_FOUND',
  // TMMAPI-0195 U2: an institution proposal already answered, withdrawn, or another than the one
  // shown (409, `details.state` NONE | DISMISSED | PROPOSED).
  INSTITUTION_PROPOSAL_STATE: 'INSTITUTION_PROPOSAL_STATE',
  // TMMAPI-0195 U3: a bank text with nothing left to name, keep or restore, or not the caller's (404).
  PAYEE_TEXT_NOT_FOUND: 'PAYEE_TEXT_NOT_FOUND',
  // TMMAPI-0181 U1 / U2 (MM-D-146): My Financial Assistant. A scope the caller can't use (422,
  // `details.invalid` / `details.reason` / `details.categories`), a change to an archived or
  // access-ended session (409), a second turn while one runs (409, `details.turnId`), a resume of a
  // turn that isn't waiting (409), a full turn queue (503), a guest share's grant (409), and a miss
  // (404, never distinguishing absent from another person's).
  ASSISTANT_SCOPE_INVALID: 'ASSISTANT_SCOPE_INVALID',
  ASSISTANT_SESSION_ARCHIVED: 'ASSISTANT_SESSION_ARCHIVED',
  ASSISTANT_ACCESS_ENDED: 'ASSISTANT_ACCESS_ENDED',
  ASSISTANT_TURN_RUNNING: 'ASSISTANT_TURN_RUNNING',
  ASSISTANT_TURN_NOT_WAITING: 'ASSISTANT_TURN_NOT_WAITING',
  ASSISTANT_BUSY: 'ASSISTANT_BUSY',
  ASSISTANT_GUEST_NOT_ALLOWED: 'ASSISTANT_GUEST_NOT_ALLOWED',
  // TMMAPI-0195 U4: a shared session's member who can't ask (403, `details.reason` NOT_ALLOWED |
  // LIMIT_REACHED | AI_OFF), on an ask, a continue, a breakout or private data. The member limit on an
  // ask keeps `409 AI_DISABLED {reason: MEMBER_LIMIT}`, which reads as LIMIT_REACHED.
  ASSISTANT_ASK_NOT_ALLOWED: 'ASSISTANT_ASK_NOT_ALLOWED',
  ASSISTANT_SESSION_NOT_FOUND: 'ASSISTANT_SESSION_NOT_FOUND',
  ASSISTANT_TURN_NOT_FOUND: 'ASSISTANT_TURN_NOT_FOUND',
  // TMMAPI-0181 U7: attachments. Over a limit (413, `details.limit` and `details.limitKind`), not an
  // image, PDF or spreadsheet by its content (415), an upload that's gone or isn't the caller's
  // (404), and one already on a message (409).
  ASSISTANT_ATTACHMENT_TOO_LARGE: 'ASSISTANT_ATTACHMENT_TOO_LARGE',
  ASSISTANT_ATTACHMENT_TYPE: 'ASSISTANT_ATTACHMENT_TYPE',
  ASSISTANT_ATTACHMENT_NOT_FOUND: 'ASSISTANT_ATTACHMENT_NOT_FOUND',
  ASSISTANT_ATTACHMENT_ATTACHED: 'ASSISTANT_ATTACHMENT_ATTACHED',
  // TMMAPI-0181 U4 / U5 (MM-D-147): Help & feedback. A change the support session never takes
  // (409), an article or help image that isn't there (404), a ticket that isn't the caller's or
  // isn't there (404), closing a ticket that can't close (409, `details.current`), an announcement
  // that isn't there (404), and no person here (404 on ensure and filing).
  ASSISTANT_SUPPORT_SESSION_FIXED: 'ASSISTANT_SUPPORT_SESSION_FIXED',
  // TMMAPI-0184 U3 (MM-D-149): an anchored session's payer (the virtual account's owner) can't pay
  // for a call someone else asks (409, `details.reason` only, never a figure).
  PAYER_UNAVAILABLE: 'PAYER_UNAVAILABLE',
  HELP_ARTICLE_NOT_FOUND: 'HELP_ARTICLE_NOT_FOUND',
  HELP_IMAGE_NOT_FOUND: 'HELP_IMAGE_NOT_FOUND',
  TICKET_NOT_FOUND: 'TICKET_NOT_FOUND',
  TICKET_TRANSITION_INVALID: 'TICKET_TRANSITION_INVALID',
  ANNOUNCEMENT_NOT_FOUND: 'ANNOUNCEMENT_NOT_FOUND',
  PERSON_NOT_FOUND: 'PERSON_NOT_FOUND',
  // TMMAPI-0183 U1 (MM-D-148): a connected AI tool that isn't the caller's, or is gone (404).
  AGENT_CONNECTION_NOT_FOUND: 'AGENT_CONNECTION_NOT_FOUND',
} as const;

/**
 * A refusal by the caller's standing on a virtual account (TMMAPI-0146): `403 FORBIDDEN` with
 * `details.requiredRole` MANAGER (a Viewer's write) or OWNER (an owner-only action), `403
 * VIRTUAL_ACCOUNT_READ_ONLY` (the owner is in grace), `403 VIRTUAL_ACCOUNT_LOCKED` (the owner is
 * locked). `null` for anything else. Never a subscription question: none of these opens purchase.
 */
export type StandingRefusal = 'needManager' | 'needOwner' | 'readOnly' | 'locked';

export function standingRefusal(error: unknown): StandingRefusal | null {
  if (!isApiError(error)) return null;
  if (error.code === ErrorCodes.VIRTUAL_ACCOUNT_READ_ONLY) return 'readOnly';
  if (error.code === ErrorCodes.VIRTUAL_ACCOUNT_LOCKED) return 'locked';
  if (error.code !== ErrorCodes.FORBIDDEN) return null;
  const backend = error.details.backend;
  const required =
    typeof backend === 'object' && backend !== null ? backend.requiredRole : undefined;
  if (required === 'MANAGER') return 'needManager';
  if (required === 'OWNER') return 'needOwner';
  return null;
}

/**
 * `409 VIRTUAL_ACCOUNT_REQUIRED` (TMMAPI-0153 §F, MM-D-135): the write needs a virtual account. The
 * served choices are `details.virtualAccounts` (`{id, name}[]`): none means "create one first",
 * several means "choose one". `null` for any other answer.
 */
export function virtualAccountRequired(
  error: unknown,
): { virtualAccounts: { id: number; name: string }[] } | null {
  if (!isApiError(error) || error.code !== ErrorCodes.VIRTUAL_ACCOUNT_REQUIRED) return null;
  const backend = error.details.backend;
  const raw =
    typeof backend === 'object' && backend !== null && Array.isArray(backend.virtualAccounts)
      ? (backend.virtualAccounts as unknown[])
      : [];
  const virtualAccounts = raw.flatMap((row) => {
    if (typeof row !== 'object' || row === null) return [];
    const { id, name } = row as { id?: unknown; name?: unknown };
    return typeof id === 'number' && typeof name === 'string' ? [{ id, name }] : [];
  });
  return { virtualAccounts };
}

/**
 * `409 OPENING_AFTER_FIRST_LINE` (TMMAPI-0160 U2): the opening must be dated before the account's
 * earliest posted line, `details.earliestPostedOn` (a civil date; null when not served). `null` for
 * any other answer.
 */
export function openingAfterFirstLine(error: unknown): { earliestPostedOn: string | null } | null {
  if (!isApiError(error) || error.code !== ErrorCodes.OPENING_AFTER_FIRST_LINE) return null;
  const backend = error.details.backend;
  const on = typeof backend === 'object' && backend !== null ? backend.earliestPostedOn : undefined;
  return { earliestPostedOn: typeof on === 'string' && on.length > 0 ? on : null };
}

/**
 * `422 BEFORE_OPENING` (TMMAPI-0196 U3; TMMAPI-0198): a new register line, a balance check's
 * adjust or a planned occurrence's Post now dated on or before the account's settled opening date,
 * `details.openingOn` (a civil date; null when not served). `null` for any other answer.
 */
export function lineBeforeOpening(error: unknown): { openingOn: string | null } | null {
  if (!isApiError(error) || error.code !== ErrorCodes.BEFORE_OPENING) return null;
  const backend = error.details.backend;
  const on = typeof backend === 'object' && backend !== null ? backend.openingOn : undefined;
  return { openingOn: typeof on === 'string' && on.length > 0 ? on : null };
}

/**
 * `409 OPENING_DIFFERS` (TMMAPI-0191): `differenceMinor` is `details.differenceMinor` (the ledger
 * less the bank, in the account's currency), null when not served. `null` for any other answer.
 */
export function openingDiffers(error: unknown): { differenceMinor: number | null } | null {
  if (!isApiError(error) || error.code !== ErrorCodes.OPENING_DIFFERS) return null;
  const backend = error.details.backend;
  const difference =
    typeof backend === 'object' && backend !== null ? backend.differenceMinor : undefined;
  return {
    differenceMinor:
      typeof difference === 'number' && Number.isInteger(difference) ? difference : null,
  };
}

/**
 * `422 CONSENT_REQUIRED` (MM-D-138, TMMAPI-0156 U1): the subscribe lacked the current terms version
 * or the auto-renewal consent. `currentTermsVersion` is `details.currentTermsVersion` (null when not
 * served). `null` for any other answer.
 */
export function consentRequired(error: unknown): { currentTermsVersion: string | null } | null {
  if (!isApiError(error) || error.code !== ErrorCodes.CONSENT_REQUIRED) return null;
  const backend = error.details.backend;
  const version =
    typeof backend === 'object' && backend !== null ? backend.currentTermsVersion : undefined;
  return {
    currentTermsVersion: typeof version === 'string' && version.length > 0 ? version : null,
  };
}

/**
 * Why AI refused a call (`409 AI_DISABLED`, `details.reason`; TMMAPI-0151, MM-D-131). The wallet's
 * reasons: `WALLET_EMPTY` (with `availableMinor` and `firstPurchaseRequired`), `NO_PRICE` and
 * `PERSON_CAP`; TMMAPI-0152's (MM-D-133) `AI_DEBT` (with `debtMinor`), `BUSY` and
 * `SUBSCRIPTION_INACTIVE`, which the prompt answers too; the gate's others (`GLOBAL`, `FEATURE`,
 * `USER`, `DAILY_CAP`, and the admin's `SUSPENDED`). `BILLING` and `MONTHLY_CAP` are retired.
 */
export const AI_WALLET_REASONS = [
  'WALLET_EMPTY',
  'NO_PRICE',
  'PERSON_CAP',
  'AI_DEBT',
  'BUSY',
  'SUBSCRIPTION_INACTIVE',
] as const;
export type AiWalletReason = (typeof AI_WALLET_REASONS)[number];

export interface AiRefusal {
  /** The served reason, verbatim; null when none is served. */
  reason: string | null;
  availableMinor: number | null;
  firstPurchaseRequired: boolean;
  /** What the wallet owes (`AI_DEBT`); null when not served. */
  debtMinor: number | null;
}

/** An `AI_DISABLED` answer's reason and figures; `null` for anything else. */
export function aiRefusal(error: unknown): AiRefusal | null {
  if (!isApiError(error) || error.code !== ErrorCodes.AI_DISABLED) return null;
  const backend = error.details.backend;
  const bag = typeof backend === 'object' && backend !== null ? backend : {};
  const available = bag.availableMinor;
  const debt = bag.debtMinor;
  return {
    reason: typeof bag.reason === 'string' && bag.reason.length > 0 ? bag.reason : null,
    availableMinor: typeof available === 'number' && Number.isFinite(available) ? available : null,
    firstPurchaseRequired: bag.firstPurchaseRequired === true,
    debtMinor: typeof debt === 'number' && Number.isFinite(debt) ? debt : null,
  };
}

/**
 * A `PAYER_UNAVAILABLE` answer's `details.reason` (TMMAPI-0184 U3, 0185 U1: `MONTHLY_LIMIT`, `BUSY`,
 * `UNAVAILABLE`, …), verbatim; null for anything else. The reason only: never a figure.
 */
export function payerUnavailableReason(error: unknown): string | null {
  if (!isApiError(error) || error.code !== ErrorCodes.PAYER_UNAVAILABLE) return null;
  const backend = error.details.backend;
  const reason = typeof backend === 'object' && backend !== null ? backend.reason : undefined;
  return typeof reason === 'string' && reason.length > 0 ? reason : null;
}

export type PatternInvalidReason = 'SYNTAX' | 'TOO_LONG' | 'TOO_SLOW';

export interface PatternInvalid {
  /** The served reason; null when none or an unknown one is served. */
  reason: PatternInvalidReason | null;
  /** The model wrote the pattern (`details.source` AI). */
  fromAi: boolean;
}

/** A `422 PATTERN_INVALID` answer's reason and source (TMMAPI-0160 U3); `null` for anything else. */
export function patternInvalid(error: unknown): PatternInvalid | null {
  if (!isApiError(error) || error.code !== ErrorCodes.PATTERN_INVALID) return null;
  const backend = error.details.backend;
  const bag = typeof backend === 'object' && backend !== null ? backend : {};
  const reason = bag.reason;
  return {
    reason: reason === 'SYNTAX' || reason === 'TOO_LONG' || reason === 'TOO_SLOW' ? reason : null,
    fromAi: bag.source === 'AI',
  };
}

/** A refusal the AI wallet prompt answers (the wallet's own reasons). */
export function isAiWalletRefusal(
  refusal: AiRefusal | null,
): refusal is AiRefusal & { reason: AiWalletReason } {
  return (
    refusal !== null && (AI_WALLET_REASONS as readonly (string | null)[]).includes(refusal.reason)
  );
}

/**
 * A response whose wire shape a normalizer cannot absorb (a detail without a numeric id) —
 * beside `MeShapeError`'s pattern (TMMAPP-0004).
 */
export class ApiShapeError extends Error {
  override readonly name = 'ApiShapeError';
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** A code every list/detail screen treats as "not found" — a miss never distinguishes absent from unshared (MM-D-05). */
export function isNotFound(error: ApiError): boolean {
  return error.status === 404;
}

/** The bank series' slice a plan line repeats (TMMAPI-0202, MM-D-154), as the reader reads it. */
export interface LikelyDuplicateDetails {
  /** Absent for a published reader with no alternate name. */
  seriesName: string | null;
  /** Full readers only (MM-D-130). */
  accountName: string | null;
  amountMinor: number | null;
  nextOn: string | null;
}

/**
 * `409 PLAN_LINE_LIKELY_DUPLICATE` (TMMAPI-0202): the slice in `details`, each part null when not
 * served. `null` for any other answer.
 */
export function likelyDuplicate(error: unknown): LikelyDuplicateDetails | null {
  if (!isApiError(error) || error.code !== ErrorCodes.PLAN_LINE_LIKELY_DUPLICATE) return null;
  const backend = error.details.backend;
  const bag: Record<string, unknown> =
    typeof backend === 'object' && backend !== null ? backend : {};
  const text = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);
  const amount = bag.amountMinor;
  return {
    seriesName: text(bag.seriesName),
    accountName: text(bag.accountName),
    amountMinor: typeof amount === 'number' && Number.isInteger(amount) ? amount : null,
    nextOn: text(bag.nextOn),
  };
}
